import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { apiService } from '../../services/apiService';
import { CallInsightsSection, ManagerReview } from './types';
import { Button } from '../ui/Button';

interface ManagerReviewPanelProps {
  sessionId: string;
  currentSection: CallInsightsSection;
  managerReview?: ManagerReview;
  isAdmin: boolean;
  onReviewSaved: () => void;
}

function formatReviewDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

const SECTION_LABELS: Record<CallInsightsSection, string> = {
  objection_faq: 'Objections / FAQs',
  landmine: 'Landmines',
  competitor: 'Competitors',
  playbook: 'Playbook',
  sales_intelligence: 'Sales Intelligence',
};

const ManagerReviewPanel: React.FC<ManagerReviewPanelProps> = ({
  sessionId,
  currentSection,
  managerReview,
  isAdmin,
  onReviewSaved,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  // Optimistic display: set immediately on save so UI doesn't flash old/empty value
  // while the parent refetches. Cleared when the updated prop arrives.
  const [optimisticText, setOptimisticText] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Prime the draft text when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setDraftText(managerReview?.section_comments?.[currentSection] ?? '');
    }
  }, [isEditing, currentSection, managerReview]);

  // Clear optimistic text once the prop catches up with the saved value
  useEffect(() => {
    if (optimisticText !== null) {
      setOptimisticText(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerReview?.section_comments?.[currentSection]]);

  const saveReview = async (sectionComments: Record<string, string>) => {
    const response = await apiService.patch(
      `/admin/sessions/${sessionId}/call-insights-review`,
      {
        // Always send the full section_comments map to avoid wiping other sections
        section_comments: sectionComments,
      }
    );
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to save review');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedSectionComments = {
        ...(managerReview?.section_comments ?? {}),
        [currentSection]: draftText,
      };
      await saveReview(updatedSectionComments);
      // Show the saved value immediately — don't wait for the parent refetch
      setOptimisticText(draftText);
      setIsEditing(false);
      onReviewSaved();
    } catch (err: any) {
      setSaveError(err?.message ?? 'An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setSaveError(null);
    try {
      const updatedSectionComments = {
        ...(managerReview?.section_comments ?? {}),
        [currentSection]: '',
      };
      await saveReview(updatedSectionComments);
      setOptimisticText('');
      onReviewSaved();
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to delete note.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setSaveError(null);
    setIsEditing(false);
  };

  const noteText = optimisticText ?? (managerReview?.section_comments?.[currentSection] ?? '');
  const hasNote = noteText.trim().length > 0;

  return (
    <div className="mt-6">
      <div className="rounded-lg border border-hairline bg-surface overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-surface-subtle border-b border-hairline">
          <div className="flex items-center gap-2">
            <span className="text-ink-secondary font-bold text-xs uppercase tracking-wide">
              Manager Notes
            </span>
            {managerReview?.reviewed_by && (
              <span className="text-xs text-ink-muted">
                — {managerReview.reviewed_by}
                {managerReview.reviewed_at && (
                  <>, {formatReviewDate(managerReview.reviewed_at)}</>
                )}
              </span>
            )}
          </div>
          {isAdmin && !isEditing && (
            <div className="flex items-center gap-2">
              <Button onClick={() => setIsEditing(true)} type="button" variant="secondary" size="sm">
                {hasNote ? 'Edit Note' : 'Add Note'}
              </Button>
              {hasNote && (
                <Button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  type="button"
                  variant="destructive"
                  size="sm"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {/* Edit form */}
          {isEditing && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-ink-secondary uppercase">
                {SECTION_LABELS[currentSection]} Note
              </label>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder={`Notes for the ${SECTION_LABELS[currentSection]} section...`}
                rows={4}
                className="w-full text-sm border border-ink-border rounded px-3 py-2 bg-surface focus:outline-none focus:ring-2 focus:ring-signature resize-y"
                autoFocus
              />
              {saveError && (
                <p className="text-sm text-error bg-error-tint border border-error rounded px-3 py-2">
                  {saveError}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={isSaving} type="button" variant="primary" size="sm">
                  {isSaving ? 'Saving...' : 'Save Note'}
                </Button>
                <Button onClick={handleCancel} disabled={isSaving} type="button" variant="secondary" size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Read-only note */}
          {!isEditing && hasNote && (
            <p className="text-sm text-ink leading-relaxed">{noteText}</p>
          )}

          {/* Empty state */}
          {!isEditing && !hasNote && (
            <p className="text-sm text-ink-muted italic">
              {isAdmin
                ? 'No manager notes yet. Click "Add Note" to add feedback.'
                : 'No manager notes for this section.'}
            </p>
          )}

          {/* Delete error shown outside edit mode */}
          {!isEditing && saveError && (
            <p className="text-sm text-error bg-error-tint border border-error rounded px-3 py-2">
              {saveError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerReviewPanel;
